// frontend/src/pages/Currency/Currency.jsx
import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { DollarSign, Plus } from "lucide-react";
import "./Currency.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Currency = () => {
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({ name: "", symbol: "$", code: "USD" });
  const [focusedFields, setFocusedFields] = useState({
    name: false,
    symbol: false,
    code: false,
  });
  const navigate = useNavigate();

  const handleFocus = (field) =>
    setFocusedFields((prev) => ({ ...prev, [field]: true }));

  const handleBlur = (field) =>
    setFocusedFields((prev) => ({
      ...prev,
      [field]: data[field]?.length > 0,
    }));

  const onChangeHandler = (e) =>
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmitHandler = async (e) => {
    e.preventDefault();

    if (!data.name.trim()) {
      toast.error("Currency name is required");
      return;
    }
    if (!data.symbol.trim()) {
      toast.error("Currency symbol is required");
      return;
    }
    if (!data.code.trim()) {
      toast.error("Currency code is required");
      return;
    }

    try {
      setSubmitting(true);

      const res = await axios.post(`${url}/api/currency`, data, {
        headers: getAuthHeaders(),
      });

      if (res.data.success) {
        toast.success("Currency added successfully!");
        setData({ name: "", symbol: "$", code: "USD" });
        navigate("/ViewCurrency");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add currency");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-wrapper">
      <div className="currency-form-container">
        <h2 className="form-title">
          <DollarSign size={20} /> Add New Currency
        </h2>

        <div className="header">
          <h2></h2>
          <button onClick={() => navigate("/ViewCurrency")} className="add-button">
            <Plus size={16} /> View Currencies
          </button>
        </div>

        <form className="currency-form" onSubmit={onSubmitHandler}>
          <div className={`form-field ${focusedFields.name || data.name ? "filled" : ""}`}>
            <input
              type="text"
              name="name"
              value={data.name}
              onChange={onChangeHandler}
              onFocus={() => handleFocus("name")}
              onBlur={() => handleBlur("name")}
              placeholder="e.g., US Dollar, Kenyan Shilling"
              required
            />
            <label>Currency Name *</label>
          </div>

          <div className={`form-field ${focusedFields.symbol || data.symbol ? "filled" : ""}`}>
            <input
              type="text"
              name="symbol"
              value={data.symbol}
              onChange={onChangeHandler}
              onFocus={() => handleFocus("symbol")}
              onBlur={() => handleBlur("symbol")}
              placeholder="e.g., $, Ksh, €"
              required
            />
            <label>Symbol *</label>
          </div>

          <div className={`form-field ${focusedFields.code || data.code ? "filled" : ""}`}>
            <input
              type="text"
              name="code"
              value={data.code}
              onChange={onChangeHandler}
              onFocus={() => handleFocus("code")}
              onBlur={() => handleBlur("code")}
              placeholder="e.g., USD, KES, EUR"
              required
            />
            <label>Code *</label>
          </div>

          <div className="form-info">
            <small>
              Currency will be available for all companies. The symbol is used in reports,
              and the code is the ISO currency code.
            </small>
          </div>

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Processing..." : "Add Currency"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Currency;